import { Box, HStack, Text } from '@chakra-ui/react'
import { faFlame } from '@fortawesome/pro-duotone-svg-icons'
import {
  faCheck,
  faCircleExclamation,
  faDoorClosed,
  faHourglassEnd,
  faSparkles,
  faUserFriends,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { pluralize } from '@/utils/helpers'

import IconWithText from '@/components/UI/IconWithText'
import Tippy from '@/components/UI/Tippy'

export type RegistrationStateType = 'reserved' | 'registered' | 'ended' | 'closed' | 'open'

export default function RegistrationState({
  state,
  numRegistrations,
  maxLearners,
  noMaxLearners = false,
  isSeries,
}: {
  state: RegistrationStateType
  numRegistrations: number
  maxLearners: number
  noMaxLearners?: boolean
  isSeries: boolean
}): JSX.Element {
  const openSeats = Math.max(maxLearners - numRegistrations, 0)

  const numRegistrationsTooltipText = noMaxLearners
    ? `${pluralize(numRegistrations, 'learner')}`
    : `${numRegistrations} of ${maxLearners} learners`
  const numRegistrationsIconText = noMaxLearners
    ? `${numRegistrations}`
    : `${numRegistrations}/${maxLearners}`

  const fractionFilled = numRegistrations / maxLearners
  const sessionAlmostFull = openSeats <= 2 || fractionFilled >= 0.8

  if (state === 'reserved') {
    return (
      <Box>
        <HStack spacing="8px" backgroundColor="yellow.subtle" padding="4px 8px" borderRadius="4px">
          <Box>
            <FontAwesomeIcon
              icon={faCircleExclamation}
              size="sm"
              color={chakraTokenToVar('fg.warning')}
            />
          </Box>
          <Text fontSize="small" fontWeight={600} color="fg.warning">
            Unconfirmed
          </Text>
        </HStack>
      </Box>
    )
  }

  if (state === 'registered') {
    return (
      <Box>
        <HStack spacing="8px" backgroundColor="bg.success" padding="4px 8px" borderRadius="4px">
          <Box>
            <FontAwesomeIcon icon={faCheck} size="sm" color={chakraTokenToVar('fg.success')} />
          </Box>
          <Text fontSize="small" fontWeight={600} color="success.fg">
            Registered
          </Text>
        </HStack>
      </Box>
    )
  }

  if (state === 'ended') {
    return (
      <Box>
        <IconWithText faIcon={faHourglassEnd} text={`${isSeries ? 'Series' : 'Session'} ended.`} />
      </Box>
    )
  }

  if (state === 'closed') {
    return (
      <Box>
        <IconWithText faIcon={faDoorClosed} text="Registration closed." />
      </Box>
    )
  }

  if (openSeats === 0) {
    return (
      <Tippy content={numRegistrationsTooltipText}>
        <Box>
          <IconWithText faIcon={faDoorClosed} text="Registration full." />
        </Box>
      </Tippy>
    )
  }

  if (sessionAlmostFull && maxLearners > 1) {
    return (
      <Tippy content={numRegistrationsTooltipText}>
        <Box>
          <IconWithText
            faIcon={faFlame}
            iconColor="yellow.solid"
            iconSecondaryColor="orange.solid"
            text={`${pluralize(openSeats, 'spot')} left!`}
          />
        </Box>
      </Tippy>
    )
  }

  if (openSeats === maxLearners && maxLearners > 1 && noMaxLearners) {
    return (
      <Tippy content={numRegistrationsTooltipText}>
        <Box>
          <IconWithText iconColor="yellow.solid" faIcon={faSparkles} text="Be the first!" />
        </Box>
      </Tippy>
    )
  }

  return (
    <Tippy content={numRegistrationsTooltipText}>
      <Box>
        <IconWithText faIcon={faUserFriends} text={numRegistrationsIconText} />
      </Box>
    </Tippy>
  )
}
